import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, InputGroup, Nav, Row, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config';
import useDebounce from '../Functions/ApiCallFunction';
import ChatPortal from '../ChatPortal';

const normalizeToken = (t) => {
  const raw = String(t ?? '').trim();
  if (!raw) return '';
  return raw.replace(/^Bearer\s+/i, '').trim();
};

const getHeaders = () => {
  const token = normalizeToken(localStorage.getItem('JWToken'));
  return {
    'Content-Type': 'application/json',
    Authorization: token ? `Bearer ${token}` : ''
  };
};

const normalize = (s) => (s || '').toString().trim().toLowerCase();

const buildListRequest = (statusFilter, presenceFilter, limit) => {
  const base = {
    includeAccepted: false,
    includePendingIncoming: false,
    includePendingOutgoing: false,
    includeDeclined: false,
    includeBlocked: false,
    showOnline: null,
    limit: limit
  };

  if (presenceFilter === 'online') base.showOnline = true;
  if (presenceFilter === 'offline') base.showOnline = false;

  switch (statusFilter) {
    case 'accepted':
      return { ...base, includeAccepted: true };
    case 'incoming':
      return { ...base, includePendingIncoming: true };
    case 'outgoing':
      return { ...base, includePendingOutgoing: true };
    case 'declined':
      return { ...base, includeDeclined: true };
    case 'blocked':
      return { ...base, includeBlocked: true };
    case 'all':
    default:
      return {
        ...base,
        includeAccepted: true,
        includePendingIncoming: true,
        includePendingOutgoing: true,
        includeDeclined: true,
        includeBlocked: true
      };
  }
};

const getFriendBadge = (f) => {
  const status = (f?.status || '').toString();

  if (status === 'Accepted') return { text: 'Friend', className: 'bg-light-green txt-dark-green' };
  if (status === 'Blocked') return { text: 'Blocked', className: 'bg-yellow txt-dark-green' };
  if (status === 'Declined') return { text: 'Declined', className: 'bg-secondary' };
  if (status === 'Pending' && f?.requestedBySelf) return { text: 'Request sent', className: 'bg-secondary' };
  if (status === 'Pending' && !f?.requestedBySelf) return { text: 'Incoming request', className: 'bg-secondary' };

  return { text: status || 'Unknown', className: 'bg-secondary' };
};

function FriendList() {
  const [activeTab, setActiveTab] = useState('friends');
  const [banner, setBanner] = useState({ type: '', message: '' });

  const [statusFilter, setStatusFilter] = useState('all');
  const [presenceFilter, setPresenceFilter] = useState('all');
  const [limit, setLimit] = useState(10);

  const [friends, setFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(false);

  const [friendSearchText, setFriendSearchText] = useState('');
  const [busy, setBusy] = useState({ uid: null, action: '' });

  const [userSearchText, setUserSearchText] = useState('');
  const debouncedUserSearch = useDebounce(userSearchText, 700);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [currentTarget, setCurrentTarget] = useState(null);

  const scrollAreaStyle = useMemo(
    () => ({
      maxHeight: '34vh',
      overflowY: 'auto',
      paddingRight: '6px'
    }),
    []
  );

  const loadFriends = async () => {
    setFriendsLoading(true);
    setBanner({ type: '', message: '' });

    try {
      const request = buildListRequest(statusFilter, presenceFilter, limit);
      const res = await axios.post(API_ENDPOINTS.GETFRIENDLIST, request, { headers: getHeaders() });
      setFriends(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err?.response?.status === 404) {
        setFriends([]);
      } else if (err?.response?.status === 401) {
        setFriends([]);
        setBanner({ type: 'danger', message: 'Unauthorized. Please log in again.' });
      } else {
        setBanner({ type: 'danger', message: 'Failed to load friends.' });
      }
    } finally {
      setFriendsLoading(false);
    }
  };

  const searchUsers = async (query) => {
    const q = normalize(query);
    if (!q) {
      setUserSearchResults([]);
      return;
    }

    setUserSearchLoading(true);
    setBanner({ type: '', message: '' });

    try {
      const res = await axios.get(API_ENDPOINTS.SEARCH_USERS, {
        params: { search: query, limit: 50 },
        headers: getHeaders()
      });
      setUserSearchResults(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setUserSearchResults([]);
      if (err?.response?.status === 401) {
        setBanner({ type: 'danger', message: 'Unauthorized. Please log in again.' });
      } else {
        setBanner({ type: 'danger', message: 'Failed to search users.' });
      }
    } finally {
      setUserSearchLoading(false);
    }
  };

  const sendFriendRequest = async (receiverUid) => {
    if (!receiverUid) return;

    setBusy({ uid: receiverUid, action: 'add' });
    setBanner({ type: '', message: '' });

    try {
      await axios.post(API_ENDPOINTS.SENDFRIENDREQUEST, null, {
        params: { receiverUid },
        headers: getHeaders()
      });
      setBanner({ type: 'success', message: 'Friend request sent.' });
      await loadFriends();
    } catch (err) {
      const msg =
        (typeof err?.response?.data === 'string' && err.response.data) ||
        err?.response?.data?.message ||
        'Failed to send friend request.';
      setBanner({ type: 'danger', message: msg });
    } finally {
      setBusy({ uid: null, action: '' });
    }
  };

  const acceptOrDeny = async (senderUid, accept) => {
    if (!senderUid) return;

    setBusy({ uid: senderUid, action: accept ? 'accept' : 'deny' });
    setBanner({ type: '', message: '' });

    try {
      await axios.put(API_ENDPOINTS.ACCEPTORDENY, null, {
        params: { senderUid, accept },
        headers: getHeaders()
      });
      await loadFriends();
      setBanner({ type: 'success', message: accept ? 'Friend request accepted.' : 'Friend request denied.' });
    } catch (err) {
      setBanner({ type: 'danger', message: 'Failed to handle friend request.' });
    } finally {
      setBusy({ uid: null, action: '' });
    }
  };

  const removeFriendship = async (friendUid) => {
    if (!friendUid) return;

    setBusy({ uid: friendUid, action: 'remove' });
    setBanner({ type: '', message: '' });

    try {
      await axios.delete(API_ENDPOINTS.REMOVEFRIEND, {
        params: { friendUid },
        headers: getHeaders()
      });
      await loadFriends();
      setBanner({ type: 'success', message: 'Removed successfully.' });
    } catch (err) {
      setBanner({ type: 'danger', message: 'Failed to remove.' });
    } finally {
      setBusy({ uid: null, action: '' });
    }
  };

  const toggleBlock = async (friendUid, blockToggle) => {
    if (!friendUid) return;

    setBusy({ uid: friendUid, action: blockToggle ? 'block' : 'unblock' });
    setBanner({ type: '', message: '' });

    try {
      await axios.put(API_ENDPOINTS.TOGGLEBLOCK, null, {
        params: { friendUid, blockToggle },
        headers: getHeaders()
      });
      await loadFriends();
      setBanner({ type: 'success', message: blockToggle ? 'User blocked.' : 'User unblocked.' });
    } catch (err) {
      setBanner({ type: 'danger', message: 'Failed to update block status.' });
    } finally {
      setBusy({ uid: null, action: '' });
    }
  };

  useEffect(() => {
    loadFriends();
  }, [statusFilter, presenceFilter, limit]);

  useEffect(() => {
    searchUsers(debouncedUserSearch);
  }, [debouncedUserSearch]);

  const visibleFriends = useMemo(() => {
    const q = normalize(friendSearchText);
    const list = Array.isArray(friends) ? [...friends] : [];

    const filtered = q ? list.filter((f) => normalize(f?.userName).includes(q)) : list;

    filtered.sort((a, b) => {
      const ao = a?.isOnline ? 1 : 0;
      const bo = b?.isOnline ? 1 : 0;
      if (bo !== ao) return bo - ao;
      return (a?.userName || '').localeCompare(b?.userName || '');
    });

    return filtered;
  }, [friends, friendSearchText]);

  const renderFriendActions = (f) => {
    const uid = f?.uid;
    const status = (f?.status || '').toString();
    const isBusy = busy.uid === uid;

    if (!uid) return null;

    const messageBtn = (userId) => (
      <Button
        size="sm"
        className="btn-icon"
        onClick={() => setCurrentTarget(userId)}
      >
        <i className="bi bi-chat-dots" />
      </Button>
    );

    const removeBtn = (
      <Button
        size="sm"
        variant="outline-danger"
        disabled={isBusy}
        onClick={() => removeFriendship(uid)}
        className="d-inline-flex align-items-center gap-2"
      >
        {isBusy && busy.action === 'remove' ? <Spinner animation="border" size="sm" /> : <i className="bi bi-trash" />}
      </Button>
    );

    if (status === 'Pending' && !f?.requestedBySelf) {
      return (
        <ButtonGroup>
          <Button
            size="sm"
            disabled={isBusy}
            className="bg-light-green txt-dark-green border-0 d-inline-flex align-items-center gap-2"
            onClick={() => acceptOrDeny(uid, true)}
          >
            {isBusy && busy.action === 'accept' ? <Spinner animation="border" size="sm" /> : <i className="bi bi-check2" />}
            Accept
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            disabled={isBusy}
            className="d-inline-flex align-items-center gap-2"
            onClick={() => acceptOrDeny(uid, false)}
          >
            {isBusy && busy.action === 'deny' ? <Spinner animation="border" size="sm" /> : <i className="bi bi-x-lg" />}
            Deny
          </Button>
        </ButtonGroup>
      );
    }

    if (status === 'Pending' && f?.requestedBySelf) {
      return (
        <ButtonGroup>
          <Button
            size="sm"
            variant="outline-danger"
            disabled={isBusy}
            onClick={() => removeFriendship(uid)}
            className="d-inline-flex align-items-center gap-2"
          >
            {isBusy && busy.action === 'remove' ? <Spinner animation="border" size="sm" /> : <i className="bi bi-x-circle" />}
            Cancel
          </Button>
        </ButtonGroup>
      );
    }

    if (status === 'Accepted') {
      return (
        <div className="d-flex justify-content-between" style={{ gap: '0.25rem' }}>
            {messageBtn(uid)}
            <Button
                size="sm"
                disabled={isBusy}
                onClick={() => toggleBlock(uid, true)}
                className="bg-danger txt-dark-green border-0 d-inline-flex align-items-center gap-2"
            >
                {isBusy && busy.action === 'block' ? <Spinner animation="border" size="sm" /> : <i className="bi-slash-circle" />}
            </Button>
            {removeBtn}
        </div>
      );
    }

    if (status === 'Blocked') {
      return (
        <ButtonGroup>
          <Button
            size="sm"
            disabled={isBusy}
            onClick={() => toggleBlock(uid, false)}
            className="bg-light-green txt-dark-green border-0 d-inline-flex align-items-center gap-2"
          >
            {isBusy && busy.action === 'unblock' ? <Spinner animation="border" size="sm" /> : <i className="bi bi-shield-check" />}
            Unblock
          </Button>
          {removeBtn}
        </ButtonGroup>
      );
    }

    return <ButtonGroup>{removeBtn}</ButtonGroup>;
  };

  const FriendRow = ({ f }) => {
    const badge = getFriendBadge(f);
    const isOnline = !!f?.isOnline;

    return (
      <div className="d-flex align-items-center justify-content-between py-2 border-bottom">
        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
          <span
            className={`rounded-circle ${isOnline ? 'bg-light-green' : 'bg-secondary'}`}
            style={{ width: 10, height: 10, display: 'inline-block', flex: '0 0 auto' }}
          />
          <div
            className="rounded-circle bg-dark-green d-flex align-items-center justify-content-center"
            style={{ width: 38, height: 38, flex: '0 0 auto' }}
          >
            <span className="txt-yellow" style={{ fontWeight: 700 }}>
              {(f?.userName || '?').toString().slice(0, 1).toUpperCase()}
            </span>
          </div>
          {/* Név és Badge konténer */}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
              <div className="text-truncate" style={{ fontWeight: 600 }}> {/* maxWidth kivéve */}
                {f?.userName || 'Unknown'}
              </div>
              {/* Flex shrink 0: ne nyomódjon össze a badge */}
              <Badge className={badge.className} style={{ flexShrink: 0 }}>{badge.text}</Badge>
            </div>
            <div className="small text-muted">{isOnline ? 'Online' : 'Offline'}</div>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-end gap-2" style={{ flex: '0 0 auto', marginLeft: '8px' }}>
          {renderFriendActions(f)}
        </div>
      </div>
    );
  };

  const renderFriendsTab = () => {
    return (
      <>
        <Row className="g-2 mb-2">
          <Col xs={12} md={5}>
            <Form.Label className="small text-muted mb-1">Filter</Form.Label>
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value || 'all')}>
              <option value="all">All</option>
              <option value="accepted">Friends</option>
              <option value="incoming">Incoming requests</option>
              <option value="outgoing">Outgoing requests</option>
              <option value="blocked">Blocked</option>
              <option value="declined">Declined</option>
            </Form.Select>
          </Col>

          <Col xs={12} md={4}>
            <Form.Label className="small text-muted mb-1">Presence</Form.Label>
            <Form.Select value={presenceFilter} onChange={(e) => setPresenceFilter(e.target.value || 'all')}>
              <option value="all">All</option>
              <option value="online">Online only</option>
              <option value="offline">Offline only</option>
            </Form.Select>
          </Col>

          <Col xs={12} md={3}>
            <Form.Label className="small text-muted mb-1">Limit</Form.Label>
            <Form.Select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10) || 10)}>
              <option value={10}>10</option>
            </Form.Select>
          </Col>
        </Row>

        <InputGroup className="mb-3">
          <InputGroup.Text>
            <i className="bi bi-search" />
          </InputGroup.Text>
          <Form.Control
            value={friendSearchText}
            onChange={(e) => setFriendSearchText(e.target.value)}
            placeholder="Search friends by username..."
            autoComplete="off"
          />
          <Button
            variant="outline-light"
            onClick={loadFriends}
            disabled={friendsLoading}
            className="d-inline-flex align-items-center gap-2"
          >
            {friendsLoading ? <Spinner animation="border" size="sm" /> : <i className="bi bi-arrow-clockwise" />}
            Refresh
          </Button>
        </InputGroup>

        <div id="FriendlistFolder" style={scrollAreaStyle}>
          {friendsLoading ? (
            <div className="py-4 text-center text-muted">
              <Spinner animation="border" />
              <div className="mt-2">Loading...</div>
            </div>
          ) : visibleFriends.length === 0 ? (
            <div className="py-3 text-center text-muted">No friends found.</div>
          ) : (
            <div>
              {visibleFriends.map((f) => (
                <FriendRow key={f?.uid || `${f?.userName}-${Math.random()}`} f={f} />
              ))}
            </div>
          )}
        </div>
      </>
    );
  };

  const renderFindUsersTab = () => {
    return (
      <>
        <Form.Label className="small text-muted mb-1">Find users</Form.Label>
        <InputGroup className="mb-2">
          <InputGroup.Text>
            <i className="bi bi-person-plus" />
          </InputGroup.Text>
          <Form.Control
            type="text"
            value={userSearchText}
            onChange={(e) => setUserSearchText(e.target.value)}
            placeholder="Type a username..."
            autoComplete="off"
          />
          {userSearchLoading && (
            <InputGroup.Text>
              <Spinner animation="border" size="sm" />
            </InputGroup.Text>
          )}
        </InputGroup>

        <div style={scrollAreaStyle}>
          {!normalize(debouncedUserSearch) ? (
            <div className="py-3 text-center text-muted">Start typing to search for users.</div>
          ) : userSearchLoading ? (
            <div className="py-3 text-center text-muted">Searching...</div>
          ) : userSearchResults.length === 0 ? (
            <div className="py-3 text-center text-muted">No users found.</div>
          ) : (
            <div>
              {userSearchResults.map((u) => {
                const uid = u?.uid || u?.Uid;
                const isBusy = busy.uid === uid;

                return (
                  <div
                    key={uid || `${u?.userName}-${Math.random()}`}
                    className="d-flex align-items-center justify-content-between py-2 border-bottom"
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }} className="text-truncate">
                        {u?.userName || u?.UserName || 'Unknown'}
                      </div>
                      <div className="small text-muted">{u?.isOnline || u?.IsOnline ? 'Online' : 'Offline'}</div>
                    </div>

                    <Button
                      size="sm"
                      disabled={isBusy}
                      onClick={() => sendFriendRequest(uid)}
                      className="bg-light-green txt-dark-green border-0 d-inline-flex align-items-center gap-2"
                    >
                      {isBusy && busy.action === 'add' ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <i className="bi bi-person-plus-fill" />
                      )}
                      Add
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <Card className="border-0 bg-transparent" style={{ transform: 'none', maxWidth: '550px' }}>
      <Card.Header className="p-0 border-0 bg-transparent">
        <Nav
          variant="tabs"
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'friends')}
          className="custom-folder-tabs border-0"
        >
          <Nav.Item>
            <Nav.Link eventKey="friends" className="fw-bold">Friends</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="find" className="fw-bold">Find Users</Nav.Link>
          </Nav.Item>
        </Nav>
      </Card.Header>

      <Card.Body className="bg-light-green text-dark-green shadow-sm rounded-bottom rounded-end p-3">
        {banner?.message ? (
          <Alert variant={banner.type || 'info'} onClose={() => setBanner({ type: '', message: '' })} dismissible>
            {banner.message}
          </Alert>
        ) : null}

        {activeTab === 'friends' ? renderFriendsTab() : renderFindUsersTab()}
      </Card.Body>
          {currentTarget && (
              <ChatPortal
                  targetUserId={currentTarget}
                  onClose={() => setCurrentTarget(null)}
              />
          )}
    </Card>
  );
}

export default FriendList;